import { v4 as uuidv4 } from 'uuid';
import {
    BaseVisualElement,
    getAllElementsRecursively,
    TextElement,
    TextStyle,
    Template,
    ElementTypes,
} from '@bynder-studio/render-core';
import { collectStyleIds } from 'packages/pages/editor/RightSideMenu/Shots/Text/TextStyles/utils';

export interface ElementProperties {
    horizontalPosition?: number;
    verticalPosition?: number;
    width?: number;
    height?: number;
}

export interface PresetElement {
    id: string | number;
    properties: ElementProperties;
    children?: Record<string, PresetElement>;
}

export interface BoundingBox {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
    width: number;
    height: number;
}

export const createTransparentBackgroundElement = (id: string | number) => ({
    id,
    type: ElementTypes.BACKGROUND_COLOR,
    name: 'Background color',
    properties: {
        elementType: ElementTypes.BACKGROUND_COLOR,
        value: {
            type: 'SOLID',
            brandColorId: null,
            red: 255,
            green: 255,
            blue: 255,
            opacity: 0,
        },
        locked: true,
        brandColors: [],
    },
});

export const calculateBoundingBox = (elements: PresetElement[]): BoundingBox => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    elements.forEach((element) => {
        const x = element.properties.horizontalPosition || 0;
        const y = element.properties.verticalPosition || 0;
        const width = element.properties.width || 0;
        const height = element.properties.height || 0;

        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x + width);
        maxY = Math.max(maxY, y + height);
    });

    return {
        minX,
        minY,
        maxX,
        maxY,
        width: Math.ceil(maxX - minX),
        height: Math.ceil(maxY - minY),
    };
};

export const calculateAspectRatio = (width: number, height: number): string => {
    const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
    const divisor = gcd(width, height);
    return `${width / divisor}:${height / divisor}`;
};

export const adjustElementPositions = <T extends PresetElement>(
    elements: T[],
    offsetX: number,
    offsetY: number,
): T[] => {
    const adjustedElements = JSON.parse(JSON.stringify(elements)) as T[];

    const adjustRecursively = (elementsToAdjust: PresetElement[]) => {
        Object.values(elementsToAdjust).forEach((element) => {
            if (element.properties) {
                if (element.properties.horizontalPosition !== undefined) {
                    element.properties.horizontalPosition -= offsetX;
                }
                if (element.properties.verticalPosition !== undefined) {
                    element.properties.verticalPosition -= offsetY;
                }
            }
            if (element.children) {
                adjustRecursively(Object.values(element.children));
            }
        });
    };

    adjustRecursively(adjustedElements);
    return adjustedElements;
};

export const buildPresetPage = (preset: Template) => {
    const boundingBox = calculateBoundingBox(preset.elements as unknown as PresetElement[]);
    const { width, height, minX, minY } = boundingBox;
    const aspectRatio = calculateAspectRatio(width, height);

    const adjustedElements = adjustElementPositions(preset.elements as unknown as PresetElement[], minX, minY);

    return {
        id: preset.id,
        name: preset.name,
        displayOrder: 0,
        width,
        height,
        aspectRatio,
        elements: [...adjustedElements, createTransparentBackgroundElement(preset.id)],
        duration: preset.duration || 0,
        frameRate: preset.frameRate || 25,
        shots: [],
    } as unknown as Template['pages'][number];
};

export type MatchList = { [key: TextStyle['uuid']]: TextStyle['uuid'] };

const collectTextStyles = (selectedGroup: BaseVisualElement[], currentStyles: TextStyle[]) => {
    const textElements = getAllElementsRecursively(selectedGroup).filter((element) => element instanceof TextElement);

    return [
        ...new Set(
            (textElements as TextElement[]).flatMap((element) =>
                [...collectStyleIds(element as TextElement)].filter((style) => !!style),
            ),
        ),
    ]
        .map((styleId) => currentStyles.find(({ uuid }) => uuid === styleId))
        .filter((style) => !!style) as TextStyle[];
};

export const convertTextStyles = (selectedGroup, currentStyles) => {
    const idsMatchList = {};

    const textStyles = collectTextStyles(selectedGroup, currentStyles).map((style) => {
        const { uuid, ...styleProps } = style;
        const newUuid = uuidv4();
        idsMatchList[uuid] = newUuid;

        return { ...styleProps, uuid: newUuid };
    });

    return [idsMatchList, textStyles];
};

export const replaceStyleIds = (
    formattedText: { runs: TextElement['formattedText']['runs'] },
    matchList: MatchList,
) => {
    return formattedText.runs.map((run) => {
        if (run.styleId && matchList[run.styleId]) {
            run.styleId = matchList[run.styleId];
        }

        return run;
    });
};

export const replaceDuplicatedIds = (elements: Template['elements'], matchList: MatchList) => {
    Object.values(elements).forEach((element) => {
        if (element?.children) {
            return replaceDuplicatedIds(element.children, matchList);
        }

        if (element?.type === 'TEXT') {
            const styleId = matchList[element.properties.styleId];

            if (styleId) {
                element.properties.styleId = styleId;
            }

            element.properties.value.runs = replaceStyleIds(element.properties.value, matchList);
        }
    });

    return elements;
};

export type Node<T> = T & { children?: Record<string, Node<T>> };

export const getAllNodesRecursively = <T extends object>(node: Node<T>) => {
    const result: Node<T>[] = [node];

    if (!node?.children) {
        return result;
    }

    Object.values(node.children).forEach((child) => {
        result.push(...getAllNodesRecursively(child));
    });

    return result;
};
